import { Photos, Property, RentalUnit } from '@/interfaces/elastic/elastic.interface';
import { useElasticStore } from '@/stores/elastic-store';
import { storeToRefs } from 'pinia';

export const useSearch = () => {
  const { getPropertyPreview, getProperties } = useElasticStore();
  const { propertyInPage, propertySelected, isLoading } = storeToRefs(useElasticStore());

  const handleGetProperties = async (page: number = 1) => {
    try {
      isLoading.value = true;
      const data = await getProperties(page);

      if (data) {
        const newProperties = JSON.parse(JSON.stringify(data));
        if (page === 1) {
          propertyInPage.value = [];
        }
        propertyInPage.value = propertyInPage.value.concat(newProperties);
      }
    } catch (error) {
      console.error('error', error);
    } finally {
      isLoading.value = false;
    }
  };

  const handleGetPropertyPublic = async (propertyPublicId: string) => {
    try {
      isLoading.value = true;
      const data = await getPropertyPreview(propertyPublicId);

      if (data) {
        propertySelected.value = data;
      }
    } catch (error) {
      console.error('error', error);
    } finally {
      isLoading.value = false;
    }
  };

  const handleGetRentalUnits = async (page: number = 1) => {
    const { getRentalUnits } = useElasticStore();
    const { rentalUnitsInPage, isLoading } = storeToRefs(useElasticStore());

    try {
      isLoading.value = true;
      const data = await getRentalUnits(page);

      if (data) {
        const newRentals = JSON.parse(JSON.stringify(data));

        if (page === 1) {
          rentalUnitsInPage.value = [];
        }
        rentalUnitsInPage.value = rentalUnitsInPage.value.concat(newRentals);
      }
    } catch (error) {
      console.error('error', error);
    } finally {
      isLoading.value = false;
    }
  };

  const getCurrentName = (rentalObj: RentalUnit, language: string) => {
    if (rentalObj?.Texts) {
      return rentalObj.Texts[`title_${language}` as keyof typeof rentalObj.Texts];
    }

    // si lo anterior no se cumple, se crea un nombre por defecto
    return createDefaultName(rentalObj);
  };

  const createDefaultName = (rentalObj: RentalUnit) => {
    // en caso de que tenga property type y locación, se construye y retorna
    const property = rentalObj.Property;
    if (property?.PropertyType && property?.Location) {
      const city = property.Location && property.Location.city ? property.Location.city : '';

      const country =
        property.Location && property.Location.city ? property.Location.country : undefined;

      return `${property.PropertyType.name_en} in ${city}${country ? `, ${country}` : ''}`;
    }
    // en caso de que no, retorna la descripción absoluta
    return rentalObj?.description;
  };

  const getCurrentNameProperty = (propertyObj: Property, language: string) => {
    if (propertyObj?.Texts) {
      return propertyObj.Texts[`title_${language}` as keyof typeof propertyObj.Texts];
    }
    return createDefaultNameProperty(propertyObj);
  };

  const createDefaultNameProperty = (propertyObj: Property) => {
    if (propertyObj?.PropertyType && propertyObj?.Location) {
      const city = propertyObj?.Location.city ?? '';
      const country = propertyObj?.Location.country ?? undefined;

      return `${propertyObj?.PropertyType.name_en} in ${city ? `${city}, ` : ' '}${country ?? ''}`;
    }
    return propertyObj?.description;
  };

  const getCoverImage = (images: Photos[]) => {
    const coverImage = images.find((image) => image.isCover);
    if (!coverImage && images.length > 0) {
      return images[0].image;
    } else {
      return coverImage ? coverImage.image : '';
    }
  };

  return {
    getCurrentName,
    getCurrentNameProperty,
    handleGetRentalUnits,
    getCoverImage,
    handleGetProperties,
    handleGetPropertyPublic,
    propertySelected,
  };
};
